import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../services/supabaseClient';
import { uploadRegistrationDocument } from '../utils/registrationDocuments';

const MAX_SIZE = 5 * 1024 * 1024;

const checkPdf = (file) => {
    if (!file) return '';
    if (file.type !== 'application/pdf') return 'Format harus PDF.';
    if (file.size > MAX_SIZE) return 'Ukuran maksimal 5MB.';
    return '';
};

// Unggah dokumen susulan dari halaman status. Hanya mengisi dokumen yang masih kosong.
// Penulisan ke database lewat fungsi add_registration_documents (hanya dua kolom dokumen,
// hanya milik sendiri, hanya selama belum Disetujui atau Ditolak).
const DokumenSusulan = () => {
    const { session } = useAuth();
    const [docs, setDocs] = useState(null);
    const [aktaFile, setAktaFile] = useState(null);
    const [kemenagFile, setKemenagFile] = useState(null);
    const [fieldError, setFieldError] = useState({ akta: '', kemenag: '' });
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const loadDocs = useCallback(async () => {
        if (!session?.user?.id) return;
        const { data, error } = await supabase
            .from('registrations')
            .select('document_url, kemenag_document_url, legal_entity_type')
            .eq('user_id', session.user.id)
            .maybeSingle();

        if (error) {
            console.error('Error fetching dokumen pendaftaran:', error);
            return;
        }
        setDocs(data);
    }, [session]);

    useEffect(() => {
        loadDocs();
    }, [loadDocs]);

    if (!docs) return null;

    const aktaKosong = !docs.document_url;
    const kemenagKosong = !docs.kemenag_document_url;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });

        if (!aktaFile && !kemenagFile) {
            setMessage({ type: 'error', text: 'Pilih minimal satu berkas PDF.' });
            return;
        }

        setIsSaving(true);
        try {
            const userId = session.user.id;
            const aktaUrl = aktaFile ? await uploadRegistrationDocument(userId, aktaFile, 'akta-sk') : null;
            const kemenagUrl = kemenagFile ? await uploadRegistrationDocument(userId, kemenagFile, 'izin-kemenag') : null;

            const { error } = await supabase.rpc('add_registration_documents', {
                p_deed_url: aktaUrl,
                p_kemenag_url: kemenagUrl
            });
            if (error) throw new Error(error.message);

            setAktaFile(null);
            setKemenagFile(null);
            setMessage({ type: 'success', text: 'Dokumen berhasil diunggah.' });
            await loadDocs();
        } catch (err) {
            console.error('Error unggah dokumen susulan:', err);
            setMessage({ type: 'error', text: 'Dokumen belum tersimpan: ' + err.message });
        } finally {
            setIsSaving(false);
        }
    };

    const handleFile = (key, setter) => (e) => {
        const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
        setFieldError((prev) => ({ ...prev, [key]: checkPdf(file) }));
        setter(file && !checkPdf(file) ? file : null);
    };

    return (
        <div className="text-left border-t border-gray-100 pt-6 mb-6">
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Dokumen pendukung</h3>

            {!aktaKosong && !kemenagKosong ? (
                <p className="text-sm text-gray-600">Akta atau SK dan izin Kemenag sudah diunggah.</p>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                    <p className="text-sm text-gray-600">
                        Dokumen berikut belum ada. Anda boleh melengkapinya sekarang atau nanti. Pengajuan tetap diproses.
                    </p>

                    {aktaKosong && (
                        <div>
                            <label htmlFor="susulanAkta" className="block text-sm font-medium text-gray-700 mb-1">
                                Akta atau SK Badan Hukum
                                {docs.legal_entity_type === 'Belum berbadan hukum' && ' (tidak diperlukan bila belum berbadan hukum)'}
                            </label>
                            <input
                                id="susulanAkta"
                                type="file"
                                accept=".pdf"
                                onChange={handleFile('akta', setAktaFile)}
                                disabled={isSaving}
                                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                            />
                            {fieldError.akta && <span className="text-xs text-red-500">{fieldError.akta}</span>}
                        </div>
                    )}

                    {kemenagKosong && (
                        <div>
                            <label htmlFor="susulanKemenag" className="block text-sm font-medium text-gray-700 mb-1">
                                Izin Kemenag, termasuk Nomor Statistik Pesantren
                            </label>
                            <input
                                id="susulanKemenag"
                                type="file"
                                accept=".pdf"
                                onChange={handleFile('kemenag', setKemenagFile)}
                                disabled={isSaving}
                                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                            />
                            {fieldError.kemenag && <span className="text-xs text-red-500">{fieldError.kemenag}</span>}
                        </div>
                    )}

                    <p className="text-xs text-gray-500">Format PDF, maksimal 5MB per berkas.</p>

                    <button
                        type="submit"
                        disabled={isSaving}
                        className={`bg-blue-600 text-white text-sm font-medium py-2 px-4 rounded-md ${isSaving ? 'opacity-70 cursor-not-allowed' : 'hover:bg-blue-700'}`}
                    >
                        {isSaving ? 'Mengunggah...' : 'Unggah dokumen'}
                    </button>
                </form>
            )}

            {message.text && (
                <p
                    role={message.type === 'error' ? 'alert' : 'status'}
                    className={`mt-3 text-sm ${message.type === 'error' ? 'text-red-600' : 'text-green-700'}`}
                >
                    {message.text}
                </p>
            )}
        </div>
    );
};

export default DokumenSusulan;
